import { Instagram, Mail, MapPin, Phone, ArrowUpRight, Clock, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import Logo from "./Logo";
import { useContent } from "@/context/ContentContext";
import { buildTelLink, buildWhatsLink, scrollToId } from "@/lib/site";

function TikTokIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 1 1-2.31-2.83v-3.5a6.37 6.37 0 1 0 5.76 6.33V8.69a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.12z" />
    </svg>
  );
}

export default function Footer() {
  const c = useContent();
  const S = c.settings;
  const tel = buildTelLink(S);
  const whats = buildWhatsLink(S);

  return (
    <footer id="kontakt" data-testid="footer-section" className="relative border-t border-white/5 bg-[#09090B]">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-20 pb-10">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between border-b border-white/5 pb-14">
          <h2 className="font-display text-2xl sm:text-4xl font-semibold tracking-tight text-zinc-100 max-w-xl">
            Bereit für einen <span className="text-blue-400">sauberen</span> Innenraum?
          </h2>
          <button
            data-testid="footer-cta-booking"
            onClick={() => scrollToId("termin")}
            className="group inline-flex h-12 shrink-0 items-center gap-2 self-start rounded-full bg-blue-600 px-7 text-sm font-semibold text-white hover:bg-blue-500 transition-colors"
          >
            Termin anfragen
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-10 pt-12 md:grid-cols-3">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <Logo className="h-10" />
            </div>
            <p className="text-sm text-zinc-300 leading-relaxed max-w-xs">
              Mobile Fahrzeugaufbereitung mit Schwerpunkt Innenraum – direkt bei dir vor Ort in{" "}
              {S.area || "deiner Nähe"}.
            </p>
            <div className="flex gap-3 mt-1">
              {S.instagram ? (
                <a
                  data-testid="footer-instagram"
                  href={S.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-zinc-800 text-zinc-400 hover:border-blue-500/50 hover:text-blue-400 transition-colors"
                >
                  <Instagram className="h-4 w-4" />
                </a>
              ) : null}
              {S.tiktok ? (
                <a
                  data-testid="footer-tiktok"
                  href={S.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-zinc-800 text-zinc-400 hover:border-blue-500/50 hover:text-blue-400 transition-colors"
                >
                  <TikTokIcon className="h-4 w-4" />
                </a>
              ) : null}
              {whats ? (
                <a
                  data-testid="footer-whatsapp"
                  href={whats}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-zinc-800 text-zinc-400 hover:border-blue-500/50 hover:text-blue-400 transition-colors"
                >
                  <MessageCircle className="h-4 w-4" />
                </a>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col gap-4 text-sm">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">Kontakt</p>
            {tel ? (
              <a
                data-testid="footer-phone"
                href={tel}
                className="flex items-center gap-3 text-zinc-200 hover:text-blue-400 transition-colors"
              >
                <Phone className="h-4 w-4 text-blue-400" /> {S.phone_display}
              </a>
            ) : null}
            <a
              data-testid="footer-email"
              href={`mailto:${S.email}`}
              className="flex items-center gap-3 text-zinc-200 hover:text-blue-400 transition-colors break-all"
            >
              <Mail className="h-4 w-4 text-blue-400" /> {S.email}
            </a>
            {S.address ? (
              <p className="flex items-start gap-3 text-zinc-200">
                <MapPin className="h-4 w-4 text-blue-400 mt-0.5" />
                <span>{S.address}</span>
              </p>
            ) : null}
            {S.hours?.map((h) => (
              <p key={h.label} className="flex items-start gap-3 text-zinc-200">
                <Clock className="h-4 w-4 text-blue-400 mt-0.5" />
                <span>
                  {h.label}: {h.value}
                </span>
              </p>
            ))}
          </div>

          <div className="flex flex-col gap-4 text-sm">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">Leistungen</p>
            {c.packages.map((p) => (
              <button
                key={p.id}
                onClick={() => scrollToId("leistungen")}
                className="text-left text-zinc-200 hover:text-blue-400 transition-colors"
              >
                {p.name}
              </button>
            ))}
            {c.addons.find((a) => a.id === "obd" && a.active !== false) ? (
              <button
                onClick={() => scrollToId("leistungen")}
                className="text-left text-zinc-200 hover:text-blue-400 transition-colors"
              >
                Fehlerspeicher auslesen & löschen
              </button>
            ) : null}
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-t border-white/5 pt-8">
          <p className="text-xs text-zinc-400">
            © {new Date().getFullYear()} {S.company_name} · Alle Rechte vorbehalten
          </p>
          <div className="flex gap-6 text-xs text-zinc-400">
            <Link data-testid="footer-link-impressum" to="/impressum" className="hover:text-blue-400 transition-colors">
              Impressum
            </Link>
            <Link data-testid="footer-link-datenschutz" to="/datenschutz" className="hover:text-blue-400 transition-colors">
              Datenschutz
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
